import { useState, useRef, useEffect } from "react";
import { Lock, MessageCircle, Download, AlertTriangle, FolderLock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createDraft } from "../../redux/ApiSlice/DraftSlice.js";
import { IoMdPlay, IoIosPause, IoMdArrowBack } from "react-icons/io";
import { HiMiniSpeakerWave, HiMiniSpeakerXMark } from "react-icons/hi2";
import { getPrivateHas } from "../../redux/ApiSlice/AccountPrivateSlice.js";
import { UploadVideoWaringModal } from "../Modals/uploadVideo/UploadVideoWaringModal.jsx";
import { uploadVideoData, Uploadvideo, clearSelectedVideo, uploadVideoToS3, addPendingUpload } from "../../redux/ApiSlice/UploadVideoSlice.js";
import { resetDraftUploadState } from "../../redux/ApiSlice/DraftSlice.js";
import { postCreatedUpdateState } from "../../redux/ApiSlice/AccountProfileSlice.js";

const MAX_VIDEO_DURATION_SECONDS = 180; // 3 minutes

const Upload = () => {
    const [count, setCount] = useState(0);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const ProfileDetails = useSelector((state) => state.AccountProfile);
    const { User_profile } = ProfileDetails;
    const { hasPin } = useSelector((state) => state.AccountPrivate);
    const uploadedVideo = useSelector((state) => state.Uploadvideo);
    const { loading } = uploadedVideo;
    const [showPrivatePopup, setShowPrivatePopup] = useState(false);
    const [isDraftSaving, setIsDraftSaving] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [categorySearch, setCategorySearch] = useState("");
    const [showCategoryError, setShowCategoryError] = useState(false);

    // Upload Settings
    const [isCheckedPrivate, setIsCheckedPrivate] = useState(false);
    const [isCheckedComment, setIsCheckedComment] = useState(true);
    const [isCheckedDownload, setIsCheckedDownload] = useState(true);

    const [activeStep, setActiveStep] = useState(0);
    const videoRef = useRef();

    const [longDurationVideo, setLongDurationVideo] = useState(false);
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(() => !open);

    const [videoThumbnail, setVideoThumbnail] = useState(null);
    const [customThumbnail, setCustomThumbnail] = useState(null);
    const [customThumbnailPreview, setCustomThumbnailPreview] = useState(null);
    const thumbnailInputRef = useRef(null);

    const [uploadVideo, setUploadVideo] = useState({
        body: "",
        video: null,
        video_url: "",
        s3_key: "",
        user_id: User_profile.id,
        is_private: isCheckedPrivate,
        comments_enabled: isCheckedComment,
        download_enabled: isCheckedDownload,
        thumbnail: null,
        is_created_video: false,
        duration: "",
    });
    const uploadTaskRef = useRef(null);
    const [isUploadingToS3, setIsUploadingToS3] = useState(false);
    const autoUploadPreviewRef = useRef("");

    const [isFileSelected, setIsFileSelected] = useState(false);

    const handleSubmitVideo = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        if (uploadedVideo.SelectedVideo.liveStream) {
            navigate("/create-livestream");
        } else {
            if (uploadVideo.s3_key) {
                try {
                    const createdPost = await dispatch(uploadVideoData(uploadVideo)).unwrap();
                    await dispatch(postCreatedUpdateState(createdPost));
                    dispatch(addPendingUpload(createdPost));
                    navigate("/account");
                } catch (error) {
                    console.error("[Upload UI] Post create failed", error);
                    alert("Unable to create post right now. Please try again.");
                    setIsSubmitting(false);
                }
            } else {
                console.warn("[Upload UI] Submit blocked: s3_key not ready yet");
                alert("Video is still processing. Please wait for upload completion.");
                setIsSubmitting(false);
            }
        }
    };

    const handleDraft = async (event) => {
        event.preventDefault();

        // Prevent multiple submissions
        if (isDraftSaving) return;

        if (!uploadVideo.s3_key || isUploadingToS3) {
            alert("Video upload is still in progress. Please wait for completion.");
            return;
        }

        try {
            setIsDraftSaving(true);
            // Don't pass uploadProgress in the data object - it's managed by Redux
            await dispatch(createDraft(uploadVideo)).unwrap();
            navigate('/draft-videos');
        } catch (error) {
            console.error('Failed to create draft:', error);
            setIsDraftSaving(false);
        }
    };

    const handleChange = (event) => {
        const inputText = event.target.value;

        // if (inputText.length <= 100) {
        setUploadVideo({ ...uploadVideo, body: inputText });
        setCount(inputText.length);
        // }
    };

    const handleSelectIntrest = (event) => {
        const inputText = event.target.value;

        if (inputText) {
            setUploadVideo({ ...uploadVideo, interest: inputText });
        }
    };
    const handleCheckboxPrivate = () => {
        if (!hasPin && !isCheckedPrivate) {
            setShowPrivatePopup(true);
            return;
        } else {
            setIsCheckedPrivate(!isCheckedPrivate);
            setUploadVideo({ ...uploadVideo, is_private: !isCheckedPrivate });
        }
    };


    const handleCheckboxComment = () => {
        setIsCheckedComment((prev) => {
            setUploadVideo((uv) => ({ ...uv, comments_enabled: !prev }));
            return !prev;
        });
    };
    const handleCheckboxDownload = () => {
        setIsCheckedDownload((prev) => {
            setUploadVideo((uv) => ({ ...uv, download_enabled: !prev }));
            return !prev;
        });
    };

    const handleThumbnailChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (customThumbnailPreview) URL.revokeObjectURL(customThumbnailPreview);
        setCustomThumbnail(file);
        setCustomThumbnailPreview(URL.createObjectURL(file));
        setUploadVideo((prev) => ({ ...prev, thumbnail: file }));
    };

    const handleRemoveThumbnail = () => {
        if (customThumbnailPreview) URL.revokeObjectURL(customThumbnailPreview);
        setCustomThumbnail(null);
        setCustomThumbnailPreview(null);
        setUploadVideo((prev) => ({ ...prev, thumbnail: null }));
        if (thumbnailInputRef.current) thumbnailInputRef.current.value = "";
    };



    const handleSubmit = () => {
        if (!uploadVideo.s3_key || isUploadingToS3) {
            alert("Video upload is still in progress. Please wait for completion.");
            return;
        }
        handleSubmitVideo();
    };

    const handleCancelUpload = () => {
        if (uploadTaskRef.current?.abort) {
            uploadTaskRef.current.abort();
        }
        autoUploadPreviewRef.current = "";
        setIsUploadingToS3(false);
        dispatch(clearSelectedVideo());
        setUploadVideo((prev) => ({
            ...prev,
            video: null,
            s3_key: "",
        }));
        setIsFileSelected(false);
    };

    const handleLoadedMetadata = () => {
        const dur = videoRef.current?.duration || 0;
        if (dur > 180) {
            setLongDurationVideo(true);
        } else {
            setLongDurationVideo(false);
        }
        const mins = Math.floor(dur / 60);
        const secs = Math.floor(dur % 60);
        setUploadVideo((prev) => ({ ...prev, duration: `${mins}.${secs.toString().padStart(2, '0')}` }));
    };

    // Function to read a video file's duration before starting upload
    const getVideoDuration = (videoFile) => {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.onloadedmetadata = () => {
                URL.revokeObjectURL(video.src);
                resolve(video.duration);
            };
            video.onerror = () => {
                URL.revokeObjectURL(video.src);
                reject(new Error('Unable to read video metadata'));
            };
            video.src = URL.createObjectURL(videoFile);
        });
    };

    // Function to generate thumbnail from video
    const generateThumbnail = (videoFile) => {
        return new Promise((resolve) => {
            const video = document.createElement('video');
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            video.addEventListener('loadedmetadata', () => {
                // Set canvas dimensions to match video
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;

                // Seek to 1 second (or 10% of video duration)
                const seekTime = Math.min(1, video.duration * 0.4);
                video.currentTime = seekTime;
            });

            video.addEventListener('seeked', () => {
                // Draw the current frame to canvas
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                // Convert canvas to blob and create URL
                canvas.toBlob((blob) => {
                    if (blob) {
                        const thumbnailURL = URL.createObjectURL(blob);
                        resolve(thumbnailURL);
                    } else {
                        resolve(null);
                    }
                }, 'image/jpeg', 0.8);
            });

            video.addEventListener('error', () => {
                resolve(null);
            });

            video.src = URL.createObjectURL(videoFile);
            video.muted = true;
            video.load();
        });
    };

    const startS3MultipartUpload = async (file) => {
        if (uploadTaskRef.current?.abort) {
            uploadTaskRef.current.abort();
        }

        setIsUploadingToS3(true);
        const uploadTask = dispatch(uploadVideoToS3({ file }));
        uploadTaskRef.current = uploadTask;
        const uploadResult = await uploadTask;

        if (uploadVideoToS3.fulfilled.match(uploadResult)) {
            setUploadVideo((prev) => ({
                ...prev,
                s3_key: uploadResult.payload?.s3_key || "",
                video_url: uploadResult.payload?.video_url || "",
            }));
        } else if (!uploadResult?.payload || uploadResult.payload !== "Upload aborted") {
            console.error("[Upload UI] S3 upload failed", {
                error: uploadResult?.payload,
            });
            alert("Failed to upload video. Please try selecting the file again.");
        } else {
            console.warn("[Upload UI] S3 upload aborted");
        }

        if (uploadTaskRef.current === uploadTask) {
            uploadTaskRef.current = null;
        }
        setIsUploadingToS3(false);
    };

    const handleFileInputChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size <= 0) {
                alert('Selected video is empty (0 bytes). Please choose a valid video file.');
                e.target.value = "";
                return;
            }

            try {
                const duration = await getVideoDuration(file);
                if (duration > MAX_VIDEO_DURATION_SECONDS) {
                    alert(`Video is too long. Please select a video that is ${MAX_VIDEO_DURATION_SECONDS / 60} minutes or shorter.`);
                    e.target.value = "";
                    return;
                }
            } catch (error) {
                console.error('[Upload UI] Failed to read video duration:', error);
                alert('Unable to read the selected video. Please try a different file.');
                e.target.value = "";
                return;
            }

            const fileURL = URL.createObjectURL(file);

            setShowCategoryError(false);
            setIsFileSelected(true);

            // Generate thumbnail for the video
            try {
                const thumbnailURL = await generateThumbnail(file);
                setVideoThumbnail(thumbnailURL);
            } catch (error) {
                console.error('Error generating thumbnail:', error);
                setVideoThumbnail(null);
            }

            // Set Redux state for preview and upload
            await dispatch(Uploadvideo({ fileURL }));
            setUploadVideo((prev) => ({
                ...prev,
                video: file,
                s3_key: "",
            }));

            await startS3MultipartUpload(file);
        }
    };

    const catogary = [
        { id: 1, value: "WHEELED ADVENTURES" },
        { id: 2, value: "FUNNY" },
        { id: 3, value: "EDUCATION" },
        { id: 4, value: "ENTERTAINMENT" },
        { id: 5, value: "FILM & ANIMATION" },
        { id: 6, value: "GAMING" },
        { id: 7, value: "HOWTO & STYLE" },
        { id: 8, value: "MOTIVATIONAL" },
        { id: 9, value: "FOOD AND COOKING" },
        { id: 10, value: "MUSIC" },
        { id: 11, value: "NEWS & POLITICS" },
        { id: 12, value: "NONPROFITS & ACTIVISM" },
        { id: 13, value: "PEOPLE & BLOGS" },
        { id: 14, value: "NATURE AND ANIMALS" },
        { id: 15, value: "SCIENCE & TECHNOLOGY" },
        { id: 16, value: "SPORTS" },
        { id: 17, value: "ADVENTURE" },
        { id: 18, value: "PARENTING AND KIDS" },
    ];

    const filteredCategories = catogary.filter(cat =>
        cat.value.toLowerCase().includes(categorySearch.toLowerCase())
    );

    const toggleButtons = [
        { id: 'private', label: 'Private', icon: <Lock size={18} />, state: isCheckedPrivate, onChange: handleCheckboxPrivate },
        { id: 'comment', label: 'Allow comments', icon: <MessageCircle size={18} />, state: isCheckedComment, onChange: handleCheckboxComment },
        { id: 'download', label: 'Allow viewer to download video', icon: <Download size={18} />, state: isCheckedDownload, onChange: handleCheckboxDownload },
    ];

    const isS3UploadReady = Boolean(uploadVideo.s3_key) && !isUploadingToS3;

    const uploadProgress = useSelector((state) => state.draft.uploadProgress); // Get uploadProgress from Redux store

    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [showControls, setShowControls] = useState(false);
    const [showThumbnailTooltip, setShowThumbnailTooltip] = useState(false);
    const [showPlayTooltip, setShowPlayTooltip] = useState(false);
    const [showMuteTooltip, setShowMuteTooltip] = useState(false);
    const fileInputRef = useRef(null);

    // Handle play/pause
    const handlePlayPause = (e) => {
        e.stopPropagation();
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
            setShowThumbnailTooltip(false);
        }
    };

    // Handle mute/unmute
    const handleMuteUnmute = (e) => {
        e.stopPropagation();
        setIsMuted((prev) => {
            if (videoRef.current) {
                videoRef.current.muted = !prev;
            }
            return !prev;
        });
    };

    // Open file input on click if no video
    const handleVideoContainerClick = () => {
        if (!uploadedVideo.SelectedVideo.fileURL && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Set muted state on video element
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.muted = isMuted;
        }
    }, [isMuted]);

    // Reset play state if video changes
    useEffect(() => {
        setIsPlaying(false);
    }, [uploadedVideo.SelectedVideo.fileURL]);

    // Auto-start upload for preselected preview URLs (for create-video flow).
    useEffect(() => {
        const selectedPreviewUrl = uploadedVideo.SelectedVideo.fileURL;

        if (!selectedPreviewUrl) return;
        if (autoUploadPreviewRef.current === selectedPreviewUrl) return;
        if (isFileSelected || uploadVideo.video || uploadVideo.s3_key || isUploadingToS3) return;

        autoUploadPreviewRef.current = selectedPreviewUrl;

        const recoverFileAndUpload = async () => {
            try {
                const response = await fetch(selectedPreviewUrl);
                const blob = await response.blob();

                if (!blob || blob.size <= 0) {
                    throw new Error("Preview URL resolved to empty blob");
                }

                const recoveredFile = new File([blob], `upload-${Date.now()}`, {
                    type: blob.type || "application/octet-stream",
                });

                const duration = await getVideoDuration(recoveredFile);
                if (duration > MAX_VIDEO_DURATION_SECONDS) {
                    alert(`Video is too long. Please select a video that is ${MAX_VIDEO_DURATION_SECONDS / 60} minutes or shorter.`);
                    autoUploadPreviewRef.current = "";
                    setIsFileSelected(false);
                    return;
                }

                setIsFileSelected(true);
                setShowCategoryError(false);

                try {
                    const thumbnailURL = await generateThumbnail(recoveredFile);
                    setVideoThumbnail(thumbnailURL);
                } catch (thumbnailError) {
                    console.error("[Upload UI] Auto-upload thumbnail generation failed", thumbnailError);
                    setVideoThumbnail(null);
                }

                setUploadVideo((prev) => ({
                    ...prev,
                    video: recoveredFile,
                    s3_key: "",
                }));

                await startS3MultipartUpload(recoveredFile);
            } catch (error) {
                console.error("[Upload UI] Auto-upload failed", error);
                alert("Unable to start upload automatically. Please tap Change Media and reselect the file.");
                autoUploadPreviewRef.current = "";
                setIsFileSelected(false);
            }
        };

        recoverFileAndUpload();
    }, [uploadedVideo.SelectedVideo.fileURL, isFileSelected, uploadVideo.video, uploadVideo.s3_key, isUploadingToS3]);

    // Update is_private and comments_enabled based on toggle changes
    useEffect(() => {
        setUploadVideo((prev) => ({
            ...prev,
            is_private: isCheckedPrivate,
            comments_enabled: isCheckedComment,
        }));
    }, [isCheckedPrivate, isCheckedComment]);

    // Clear selected video on unmount only
    useEffect(() => {
        // Get private folder status on mount
        dispatch(getPrivateHas())
        // Reset draft upload state to ensure clean slate
        dispatch(resetDraftUploadState())

        return () => {
            if (uploadTaskRef.current?.abort) {
                uploadTaskRef.current.abort();
            }
            dispatch(clearSelectedVideo());
        };
    }, [dispatch]);

    // Revoke thumbnail blob URLs when they change or on unmount
    useEffect(() => {
        return () => {
            if (videoThumbnail) URL.revokeObjectURL(videoThumbnail);
        };
    }, [videoThumbnail]);

    useEffect(() => {
        return () => {
            if (customThumbnailPreview) URL.revokeObjectURL(customThumbnailPreview);
        };
    }, [customThumbnailPreview]);

    return (
        <div className="absolute bottom-0 w-full h-full bg-[#fff] dark:bg-[#1E1D21] overflow-hidden">
            {/* Always-mounted thumbnail input so ref is available on all steps */}
            <input
                type="file"
                accept="image/*"
                ref={thumbnailInputRef}
                className="hidden"
                onChange={handleThumbnailChange}
            />

            {activeStep === 0 &&
                <div className="sticky top-0 z-50">
                    <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2">
                        <IoMdArrowBack
                            onClick={() => navigate("/create-videos")}
                            className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[24px] text-black cursor-pointer"
                        />
                        <p className="text-center font-bold text-[16px] text-black flex-grow">Upload Video</p>
                    </div>
                </div>
            }

            {!(activeStep == 1) && (
                <div className="h-[50%] flex flex-col items-center justify-center">
                    <div
                        className="h-[38vh] w-[55%] rounded-md border border-gray-400 dark:border-gray-800 flex flex-col items-center justify-center relative bg-black dark:bg-[#333333] cursor-pointer"
                        onClick={handleVideoContainerClick}
                        onMouseEnter={() => setShowControls(true)}
                        onMouseLeave={() => setShowControls(false)}
                    >
                        <input
                            type="file"
                            accept="video/*"
                            className="hidden"
                            ref={fileInputRef}
                            onChange={handleFileInputChange}
                        />
                        {uploadedVideo.SelectedVideo.fileURL ? (
                            <>
                                <div className="flex items-center justify-center w-full h-full">
                                    <video
                                        ref={videoRef}
                                        src={uploadedVideo.SelectedVideo.fileURL}
                                        poster={!customThumbnailPreview ? videoThumbnail : undefined}
                                        muted={isMuted}
                                        controls={false}
                                        disableRemotePlayback
                                        disablePictureInPicture
                                        onPlay={() => setIsPlaying(true)}
                                        onPause={() => setIsPlaying(false)}
                                        onLoadedMetadata={handleLoadedMetadata}
                                        // className="h-full w-full object-none rounded-md"
                                        className="w-auto h-auto max-w-full object-fit max-h-full rounded-md"
                                    />
                                </div>

                                {/* Custom thumbnail overlay — shown when not playing */}
                                {customThumbnailPreview && !isPlaying && (
                                    <div className="absolute inset-0 rounded-md overflow-hidden pointer-events-none">
                                        <img
                                            src={customThumbnailPreview}
                                            alt="thumbnail preview"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                )}

                                {showControls && (
                                    <div className="absolute bottom-2 left-2 flex gap-2 z-10">
                                        {/* Play / Pause */}
                                        <div className="relative flex items-center">
                                            <button
                                                onClick={handlePlayPause}
                                                onMouseEnter={() => setShowPlayTooltip(true)}
                                                onMouseLeave={() => setShowPlayTooltip(false)}
                                                className="bg-white/80 dark:bg-black/80 rounded-full h-6 w-6 flex items-center justify-center"
                                            >
                                                {isPlaying ? (
                                                    <IoIosPause className="text-[15px] text-black dark:text-white" />
                                                ) : (
                                                    <IoMdPlay className="text-[15px] ml-[3px] text-black dark:text-white" />
                                                )}
                                            </button>
                                            {showPlayTooltip && (
                                                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-black/80 text-white text-[10px] px-2 py-1 rounded-md pointer-events-none z-20">
                                                    {isPlaying ? "Pause" : "Play"}
                                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-black/80" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Mute / Unmute */}
                                        <div className="relative flex items-center">
                                            <button
                                                onClick={handleMuteUnmute}
                                                onMouseEnter={() => setShowMuteTooltip(true)}
                                                onMouseLeave={() => setShowMuteTooltip(false)}
                                                className="bg-white/80 dark:bg-black/80 rounded-full h-6 w-6 flex items-center justify-center"
                                            >
                                                {isMuted ? (
                                                    <HiMiniSpeakerXMark className="text-[15px] text-black dark:text-white" />
                                                ) : (
                                                    <HiMiniSpeakerWave className="text-[15px] text-black dark:text-white" />
                                                )}
                                            </button>
                                            {showMuteTooltip && (
                                                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-black/80 text-white text-[10px] px-2 py-1 rounded-md pointer-events-none z-20">
                                                    {isMuted ? "Unmute" : "Mute"}
                                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-black/80" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Thumbnail button */}
                                        <div className="relative flex items-center">
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); thumbnailInputRef.current?.click(); }}
                                                onMouseEnter={() => setShowThumbnailTooltip(true)}
                                                onMouseLeave={() => setShowThumbnailTooltip(false)}
                                                className="bg-white/80 dark:bg-black/80 rounded-full h-6 w-6 flex items-center justify-center"
                                            >
                                                {customThumbnailPreview ? (
                                                    <img src={customThumbnailPreview} alt="thumb" className="h-full w-full object-cover rounded-full" />
                                                ) : (
                                                    <svg className="w-3.5 h-3.5 text-black dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="2" />
                                                        <path d="M3 9l4-4 4 4 4-6 4 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
                                                    </svg>
                                                )}
                                            </button>
                                            {showThumbnailTooltip && (
                                                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-black/80 text-white text-[10px] px-2 py-1 rounded-md pointer-events-none z-20">
                                                    {customThumbnailPreview ? "Change thumbnail" : "Add thumbnail"}
                                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-black/80" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Remove thumbnail button */}
                                        {customThumbnailPreview && (
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); handleRemoveThumbnail(); }}
                                                className="bg-red-500/80 rounded-full h-6 w-6 flex items-center justify-center text-white text-[11px] font-bold"
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                )}
                                {/* Change Media Button */}
                                <button
                                    type="button"
                                    className="absolute top-2 right-2 bg-white/80 dark:bg-[#333333] text-black dark:text-white px-3 py-1 rounded-full text-xs font-semibold shadow hover:bg-white hover:text-black border border-gray-300 dark:border-gray-800/50"
                                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                >
                                    Change Media
                                </button>
                            </>
                        ) : (
                            <div className="w-full h-full text-[10px] flex items-center justify-center text-gray-400 dark:text-gray-500">
                                Select a video to upload
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Only show form and bottom nav if a video is selected */}
            {uploadedVideo.SelectedVideo.fileURL && (
                <>
                    <form className="w-full border-t border-gray-400 dark:border-gray-800/50">
                        {activeStep === 0 &&
                            <div className="flex w-full items-center justify-center cursor-pointer mt-3">
                                <p className="text-[16px] font-bold text-black dark:text-white">
                                    Your Caption
                                </p>
                            </div>
                        }
                        {activeStep === 0 && (
                            <div className="absolute pb-6 overflow-y-scroll w-[100%] mt-2 px-2 left-1/2 transform -translate-x-1/2">
                                <div className="relative">
                                    <textarea
                                        rows="4"
                                        maxLength={200}
                                        onChange={handleChange}
                                        placeholder="Type caption here..."
                                        className="w-full p-2 text-[14px] focus:outline-none resize-none bg-gray-100 dark:bg-[#333333] rounded-md text-black dark:text-white"
                                    />
                                    <p className="text-[12px] absolute right-0 -bottom-3 text-black dark:text-white">
                                        {count}/200
                                    </p>
                                </div>
                                {!isS3UploadReady && isFileSelected && (
                                    <p className="mt-5 text-center text-[12px] text-gray-600 dark:text-gray-300">
                                        {isUploadingToS3 ? "Uploading video to secure storage..." : "Finishing video processing..."}
                                    </p>
                                )}
                            </div>
                        )}
                        {activeStep === 1 && (
                            <div className="w-full h-[-webkit-fill-available] overflow-scroll no-scrollbar">
                                {/* Header */}
                                <div className="sticky top-0 z-50 bg-white dark:bg-black">
                                    <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2">
                                        <IoMdArrowBack
                                            onClick={() => setActiveStep(0)}
                                            className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[24px] text-black cursor-pointer"
                                        />
                                        <p className="text-center font-bold text-[16px] text-black flex-grow">Upload Settings</p>
                                    </div>
                                </div>

                                {/* Add category button - hidden in UI, interest field kept as null */}
                                {/* {false && (
                                    <div
                                        onClick={() => setShowCategoryModal(true)}
                                        className="flex items-center justify-between px-4 py-4 mb-3 mx-4 mt-4 bg-white dark:bg-[#1a1a1a] border-2 border-[#BDE6FF] rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-[#222]"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="flex items-center justify-center w-6 h-6">
                                                <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <rect x="3" y="3" width="7" height="7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    <rect x="14" y="3" width="7" height="7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    <rect x="14" y="14" width="7" height="7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    <rect x="3" y="14" width="7" height="7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-[14px] font-semibold dark:text-white">Add category</p>
                                                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                                    {uploadVideo.interest || "Select the best category for your video."}
                                                </p>
                                            </div>
                                        </div>
                                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                )} */}

                                {/* Toggle switches */}
                                {toggleButtons.map((button) => (
                                    <div key={button.id} className="flex justify-between items-center px-4 py-4 bg-white dark:bg-[#1E1D21]">
                                        <div className="flex items-center gap-3">
                                            <div className="text-black dark:text-white text-[18px]">
                                                {button.icon}
                                            </div>
                                            <p className="text-[14px] font-medium text-black dark:text-white">{button.label}</p>
                                        </div>
                                        <label className={`autoSaverSwitch relative inline-flex select-none items-center ${isSubmitting ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
                                            <input
                                                type="checkbox"
                                                name={button.id}
                                                className="sr-only"
                                                checked={button.state}
                                                onChange={button.onChange}
                                                disabled={isSubmitting}
                                            />
                                            <span className={`slider flex h-[22px] w-[42px] items-center rounded-full p-1 duration-200
                                                ${button.state ? "bg-[#BDE6FF]" : "bg-[#D1D1D6]"}`}
                                            >
                                                <span className={`dot h-[16px] w-[16px] rounded-full bg-white dark:bg-[#333333] duration-200 shadow-md
                                                    ${button.state ? "translate-x-[18px]" : ""}`}
                                                />
                                            </span>
                                        </label>
                                    </div>
                                ))}

                            </div>
                        )}
                    </form>

                    {/* {loading && (
                        <ProgresBar width={uploadedVideo.progress} />
                    )} */}

                    {/* Bottom Navigation */}
                    <div className="absolute bottom-0 w-full flex gap-3 px-4 py-3 bg-white dark:bg-[#1E1D21] border-t border-gray-200 dark:border-gray-800/50">
                        {activeStep === 1 && (
                            <>
                                <button
                                    onClick={(event) => handleDraft(event)}
                                    disabled={isDraftSaving || isSubmitting || isUploadingToS3 || (uploadProgress > 0 && uploadProgress < 100)}
                                    className={`flex-1 rounded-xl text-[14px] font-semibold h-10 border-2 relative overflow-hidden ${isDraftSaving || isSubmitting || isUploadingToS3 || (uploadProgress > 0 && uploadProgress < 100)
                                        ? "bg-gray-200 text-black border-[#BDE6FF] cursor-not-allowed"
                                        : "bg-white dark:bg-[#333333] text-black dark:text-white border-gray-600 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-[#444444]"
                                        }`}
                                >
                                    {(isDraftSaving || (uploadProgress > 0 && uploadProgress < 100)) && uploadProgress > 0 && (
                                        <div
                                            className="absolute left-0 top-0 h-full bg-[#BDE6FF] transition-all duration-300"
                                            style={{ width: `${uploadProgress}%` }}
                                        />
                                    )}
                                    <span className="relative z-10">
                                        {isDraftSaving || (uploadProgress > 0 && uploadProgress < 100)
                                            ? uploadProgress > 0
                                                ? `Saving... ${uploadProgress}%`
                                                : "Saving..."
                                            : "Save draft"
                                        }
                                    </span>
                                </button>
                                <button
                                    type="submit"
                                    onClick={longDurationVideo ? handleOpen : handleSubmit}
                                    disabled={loading || isUploadingToS3 || isSubmitting}
                                    className={`flex-1 rounded-xl text-[14px] font-semibold h-10 relative overflow-hidden ${loading || isSubmitting ? "bg-gray-200 text-black border-2 border-[#BDE6FF] cursor-not-allowed" : "bg-[#BDE6FF] hover:bg-[#9dd5f0]"
                                        } text-black`}
                                >
                                    {loading && uploadedVideo.progress > 0 && (
                                        <div
                                            className="absolute left-0 top-0 h-full bg-[#BDE6FF] transition-all duration-300"
                                            style={{ width: `${uploadedVideo.progress}%` }}
                                        />
                                    )}
                                    <span className="relative z-10">
                                        {loading ? `Uploading... ${uploadedVideo.progress}%` : isSubmitting ? "Redirecting..." : "Upload"}
                                    </span>
                                    <UploadVideoWaringModal
                                        handleOpen={handleOpen}
                                        open={open}
                                        message={"Your video is longer than 3 min."}
                                    />
                                </button>
                                {isUploadingToS3 && (
                                    <button
                                        type="button"
                                        onClick={handleCancelUpload}
                                        className="flex-1 rounded-xl text-[14px] font-semibold h-10 bg-red-100 hover:bg-red-200 text-red-700"
                                    >
                                        Cancel Upload
                                    </button>
                                )}
                            </>
                        )}
                        {activeStep === 0 && (
                            <button
                                onClick={() => setActiveStep(1)}
                                disabled={!isS3UploadReady}
                                className={`w-full rounded-xl text-[14px] font-semibold h-10 text-black ${isS3UploadReady ? "bg-[#BDE6FF] hover:bg-[#9dd5f0]" : "bg-gray-200 cursor-not-allowed"}`}
                            >
                                {isS3UploadReady ? "Next" : "Please wait..."}
                            </button>
                        )}
                    </div>
                </>
            )}

            {/* Category Selection Modal */}
            {showCategoryModal && (
                <div className="absolute inset-0 z-50 bg-white dark:bg-[#1E1D21]">
                    <div className="h-full flex flex-col">
                        {/* Header */}
                        <div className="sticky top-0 z-50">
                            <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2">
                                <IoMdArrowBack
                                    onClick={() => setShowCategoryModal(false)}
                                    className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[24px] text-black cursor-pointer"
                                />
                                <p className="text-center font-bold text-[16px] text-black flex-grow">Add category</p>
                            </div>
                        </div>

                        {/* Search Bar */}
                        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800/50">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search"
                                    value={categorySearch}
                                    onChange={(e) => setCategorySearch(e.target.value)}
                                    className="w-full px-4 py-2 pl-4 pr-10 bg-gray-100 dark:bg-[#333333] rounded-lg text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-300 text-black dark:text-white"
                                />
                                <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                        </div>

                        {/* Category List */}
                        <div className="flex-1 overflow-y-auto">
                            {filteredCategories.map((item) => (
                                <div
                                    key={item.id}
                                    onClick={() => {
                                        setUploadVideo({ ...uploadVideo, interest: item.value });
                                        setShowCategoryModal(false);
                                    }}
                                    className="px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#444444]"
                                >
                                    <p className="text-[14px] text-black dark:text-white">{item.value}</p>
                                </div>
                            ))}
                        </div>

                        {/* Done Button */}
                        <div className="p-4">
                            <button
                                onClick={() => setShowCategoryModal(false)}
                                className="w-full rounded-xl bg-[#BDE6FF] hover:bg-[#9dd5f0] text-[14px] font-semibold h-10 text-black"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showPrivatePopup && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white w-[80%] max-w-sm dark:bg-gray-900 px-6 py-4 rounded-xl shadow-lg text-center">
                        <div className="mb-3">
                            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                <FolderLock className="w-6 h-6 text-red-600" />
                            </div>
                        </div>
                        <p className="text-[18px] font-bold mb-2 text-gray-900 dark:text-white">Private Folder Not Created</p>
                        <p className="mb-6 text-[13px] text-gray-600 dark:text-gray-300">
                            You need to create a private folder before you can upload private videos.
                        </p>
                        <div className="flex flex-col gap-3">
                            <button
                                className="flex-1 text-[14px] bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold px-6 py-2 rounded-full transition-colors"
                                onClick={() => setShowPrivatePopup(false)}
                            >
                                Skip
                            </button>
                            <button
                                className="flex-1 text-[14px] bg-[#BDE6FF] hover:bg-[#9dd5f0] text-black font-semibold px-6 py-2 rounded-full transition-colors"
                                onClick={() => {
                                    setShowPrivatePopup(false);
                                    navigate("/account/private/empty");
                                }}
                            >
                                Create Now
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showCategoryError && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white w-[80%] max-w-sm dark:bg-gray-900 px-6 py-4 rounded-xl shadow-lg text-center">
                        <div className="mb-3">
                            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                <AlertTriangle className="w-6 h-6 text-red-600" />
                            </div>
                        </div>
                        <p className="text-[18px] font-bold mb-2 text-gray-900 dark:text-white">Category Required</p>
                        <p className="mb-6 text-[13px] text-gray-600 dark:text-gray-300">
                            Please select a category for your video before uploading.
                        </p>
                        <button
                            onClick={() => setShowCategoryError(false)}
                            className="w-full bg-[#BDE6FF] hover:bg-[#9dd5f0] text-black font-semibold px-6 py-2 rounded-full transition-colors"
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Upload;
